import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Check, X, AlertCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { CURRENCIES } from '@/utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Register({ onLogin }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({
    email: false,
    password: false,
    confirmPassword: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  // Validation states
  const [validations, setValidations] = useState({
    emailValid: false,
    passwordLength: false,
    passwordUppercase: false,
    passwordLowercase: false,
    passwordNumber: false,
    passwordsMatch: false
  });

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password validation
  useEffect(() => {
    setValidations({
      emailValid: validateEmail(email),
      passwordLength: password.length >= 8,
      passwordUppercase: /[A-Z]/.test(password),
      passwordLowercase: /[a-z]/.test(password),
      passwordNumber: /[0-9]/.test(password),
      passwordsMatch: password === confirmPassword && password.length > 0 && confirmPassword.length > 0
    });
  }, [email, password, confirmPassword]);

  // Check if form is valid
  const isFormValid = () => {
    return (
      name.length > 0 &&
      validations.emailValid &&
      validations.passwordLength &&
      validations.passwordUppercase &&
      validations.passwordLowercase &&
      validations.passwordNumber &&
      validations.passwordsMatch
    );
  };

  // Get password strength
  const getPasswordStrength = () => {
    let strength = 0;
    if (validations.passwordLength) strength++;
    if (validations.passwordUppercase) strength++;
    if (validations.passwordLowercase) strength++;
    if (validations.passwordNumber) strength++;
    
    if (strength <= 1) return { label: 'Weak', color: 'text-red-500' };
    if (strength <= 3) return { label: 'Medium', color: 'text-yellow-500' };
    return { label: 'Strong', color: 'text-green-500' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Final validation check
    if (!isFormValid()) {
      toast.error('Please fix all validation errors before submitting');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, {
        name,
        email,
        password,
        currency
      });
      
      // Clear any existing onboarding status for new user
      localStorage.removeItem('onboarding_completed');
      
      toast.success('Registration successful!');
      onLogin(response.data.token, response.data.user);
      
      // Navigate to onboarding for new users
      navigate('/onboarding');
    } catch (error) {
      // Handle duplicate email error
      const errorMessage = error.response?.data?.detail || 'Registration failed';
      if (errorMessage.includes('already exists') || errorMessage.includes('duplicate') || errorMessage.includes('already registered')) {
        toast.error('This email is already registered. Please login instead.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-900">
      <div className="w-full max-w-md">
        {/* Back to Home */}
        <Link 
          to="/" 
          className="inline-flex items-center text-gray-400 hover:text-gray-300 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <Card className="w-full p-8 bg-gray-800/90 backdrop-blur-xl border border-gray-700 shadow-2xl" data-testid="register-card">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mb-4">
              <span className="text-white font-bold text-2xl">FA</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-100 mb-1" data-testid="register-title">
              FinAI
            </h1>
            <p className="text-xl font-semibold text-gray-300 mb-2">Create Account</p>
            <p className="text-gray-400 text-sm">Start your financial journey today</p>
          </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="name" className="text-gray-300">Full Name</Label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="mt-2 bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
              placeholder="John Doe"
              data-testid="name-input"
            />
          </div>

          {/* Email with validation */}
          <div>
            <Label htmlFor="email" className="text-gray-300">Email</Label>
            <div className="relative">
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur('email')}
                required
                className={`mt-2 bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 pr-10 ${
                  touched.email && !validations.emailValid ? 'border-red-500' : 
                  touched.email && validations.emailValid ? 'border-green-500' : ''
                }`}
                placeholder="your@email.com"
                data-testid="email-input"
              />
              {touched.email && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 mt-1">
                  {validations.emailValid ? (
                    <Check className="w-5 h-5 text-green-500" />
                  ) : (
                    <X className="w-5 h-5 text-red-500" />
                  )}
                </div>
              )}
            </div>
            {touched.email && !validations.emailValid && (
              <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Please enter a valid email address
              </p>
            )}
          </div>

          {/* Password with strength indicator */}
          <div>
            <Label htmlFor="password" className="text-gray-300">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => handleBlur('password')}
                required
                className="mt-2 bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 pr-10"
                placeholder="••••••••"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 mt-1 text-gray-400 hover:text-gray-300"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            {/* Password strength indicator */}
            {password.length > 0 && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-400">Password Strength:</span>
                  <span className={`text-xs font-semibold ${getPasswordStrength().color}`}>
                    {getPasswordStrength().label}
                  </span>
                </div>
                
                {/* Requirements checklist */}
                <div className="space-y-1 text-xs">
                  <div className={`flex items-center gap-2 ${validations.passwordLength ? 'text-green-400' : 'text-gray-400'}`}>
                    {validations.passwordLength ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    <span>At least 8 characters</span>
                  </div>
                  <div className={`flex items-center gap-2 ${validations.passwordUppercase ? 'text-green-400' : 'text-gray-400'}`}>
                    {validations.passwordUppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    <span>One uppercase letter</span>
                  </div>
                  <div className={`flex items-center gap-2 ${validations.passwordLowercase ? 'text-green-400' : 'text-gray-400'}`}>
                    {validations.passwordLowercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    <span>One lowercase letter</span>
                  </div>
                  <div className={`flex items-center gap-2 ${validations.passwordNumber ? 'text-green-400' : 'text-gray-400'}`}>
                    {validations.passwordNumber ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    <span>One number</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <Label htmlFor="confirmPassword" className="text-gray-300">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => handleBlur('confirmPassword')}
                required
                className={`mt-2 bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 pr-10 ${
                  touched.confirmPassword && !validations.passwordsMatch && confirmPassword.length > 0 ? 'border-red-500' : 
                  touched.confirmPassword && validations.passwordsMatch ? 'border-green-500' : ''
                }`}
                placeholder="••••••••"
                data-testid="confirm-password-input"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 mt-1 flex items-center gap-2">
                {touched.confirmPassword && confirmPassword.length > 0 && (
                  validations.passwordsMatch ? (
                    <Check className="w-5 h-5 text-green-500" />
                  ) : (
                    <X className="w-5 h-5 text-red-500" />
                  )
                )}
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="text-gray-400 hover:text-gray-300"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            {touched.confirmPassword && !validations.passwordsMatch && confirmPassword.length > 0 && (
              <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Passwords do not match
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="currency" className="text-gray-300">Preferred Currency</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="mt-2 bg-gray-700 border-gray-600 text-white" data-testid="currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600 text-white">
                {Object.entries(CURRENCIES).map(([code, curr]) => (
                  <SelectItem key={code} value={code} className="text-white focus:bg-gray-600 focus:text-white">
                    {curr.symbol} {curr.name} ({code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            type="submit"
            disabled={loading || !isFormValid()}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium py-6 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="register-button"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>

        <p className="text-center mt-6 text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className="text-teal-400 hover:text-teal-300 font-medium" data-testid="login-link">
            Sign in
          </Link>
        </p>
      </Card>
      </div>
    </div>
  );
}